/**
 * WebsitePreview - Component for showing website generation progress and preview
 *
 * When complete or compact mode: Shows horizontal success banner matching campaign-success style
 * During generation: Shows progress steps with iframe preview
 */

import React, { FC, useState, useRef } from 'react';
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Progress,
  Button,
  Badge,
  Spinner,
  Link,
  useColorModeValue,
} from '@chakra-ui/react';

interface WebsitePreviewProps {
  status?: 'pending' | 'generating_content' | 'generating_images' | 'complete' | 'failed';
  domain?: string;
  url?: string;
  compact?: boolean;
}

// Step configuration for progress view
const STEPS = [
  { key: 'generating_content', label: 'Creating Content', description: 'Generating text and layout...' },
  { key: 'generating_images', label: 'Creating Images', description: 'Generating images with AI...' },
  { key: 'complete', label: 'Complete', description: 'Your website is ready!' },
];

const CheckIcon: FC = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.3332 4L5.99984 11.3333L2.6665 8"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StepIndicator: FC<{
  step: typeof STEPS[0];
  index: number;
  currentStepIndex: number;
  isComplete: boolean;
  isFailed: boolean;
}> = ({ step, index, currentStepIndex, isComplete, isFailed }) => {
  const isActive = index === currentStepIndex;
  const isPast = index < currentStepIndex || isComplete;

  const activeColor = 'orange.500';
  const completedColor = 'green.500';
  const inactiveColor = 'gray.300';
  const failedColor = 'red.500';

  const getColor = () => {
    if (isFailed && isActive) return failedColor;
    if (isPast) return completedColor;
    if (isActive) return activeColor;
    return inactiveColor;
  };

  return (
    <Flex align="center" gap={3}>
      <Flex
        align="center"
        justify="center"
        w="32px"
        h="32px"
        borderRadius="full"
        bg={isPast ? completedColor : 'white'}
        border="2px solid"
        borderColor={getColor()}
        transition="all 0.3s ease"
      >
        {isPast ? (
          <Box color="white">
            <CheckIcon />
          </Box>
        ) : isActive ? (
          isFailed ? (
            <Text color={failedColor} fontWeight="bold">✕</Text>
          ) : (
            <Spinner size="sm" color={activeColor} />
          )
        ) : (
          <Text fontSize="sm" fontWeight="bold" color={inactiveColor}>
            {index + 1}
          </Text>
        )}
      </Flex>

      <VStack align="start" spacing={0}>
        <Text
          fontSize="sm"
          fontWeight={isActive || isPast ? 'semibold' : 'normal'}
          color={isActive ? 'gray.800' : isPast ? 'green.600' : 'gray.400'}
          fontFamily="'Noto Sans', 'Open Sans', sans-serif"
        >
          {step.label}
        </Text>
        {isActive && (
          <Text
            fontSize="xs"
            color={isFailed ? 'red.500' : 'gray.500'}
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
          >
            {isFailed ? 'An error occurred' : step.description}
          </Text>
        )}
      </VStack>
    </Flex>
  );
};

const WebsitePreview: FC<WebsitePreviewProps> = ({
  status = 'pending',
  domain = '',
  url = '',
  compact = false,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [showFullPreview, setShowFullPreview] = useState(false);

  const parsedStatus = status as WebsitePreviewProps['status'];
  const parsedDomain = domain ? String(domain) : '';
  const parsedUrl = url ? String(url) : '';
  // If only URL is provided (no status/domain), treat as completed website
  const hasOnlyUrl = parsedUrl && parsedStatus === 'pending' && !parsedDomain;
  const isCompact = compact || hasOnlyUrl;

  const displayDomain = parsedDomain || (parsedUrl ? parsedUrl.replace(/^https?:\/\//, '').split('/')[0] : '');

  const getCurrentStepIndex = () => {
    switch (parsedStatus) {
      case 'pending':
        return -1;
      case 'generating_content':
        return 0;
      case 'generating_images':
        return 1;
      case 'complete':
        return 2;
      case 'failed':
        return 0;
      default:
        return -1;
    }
  };

  const currentStepIndex = getCurrentStepIndex();
  const isComplete = parsedStatus === 'complete';
  const isFailed = parsedStatus === 'failed';

  const progressPercent = isComplete ? 100 : ((currentStepIndex + 1) / STEPS.length) * 100;

  const handleIframeLoad = () => {
    setIframeLoaded(true);
  };

  const showIframe = (parsedStatus === 'generating_images' || isComplete) && parsedUrl;

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.800');

  // SUCCESS STATE: Horizontal banner matching campaign-success style
  // This applies to both explicit complete status AND compact mode (URL only = completed website)
  if ((isComplete || isCompact) && parsedUrl) {
    return (
      <Flex
        w="100%"
        maxW="420px"
        bg="green.50"
        borderRadius="lg"
        border="1px solid"
        borderColor="green.200"
        p={3}
        my={2}
        direction={{ base: 'column', sm: 'row' }}
        align={{ base: 'flex-start', sm: 'center' }}
        gap={3}
      >
        <Flex align="center" gap={3} minW={0} flex={1}>
          {/* Success Icon */}
          <Box
            w="36px"
            h="36px"
            bg="green.500"
            borderRadius="full"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 6L9 17L4 12" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>

          {/* Text Content */}
          <Box flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="green.800" lineHeight="short">
              Website Created!
            </Text>
            <Text fontSize="xs" color="green.700" noOfLines={1} title={displayDomain}>
              {displayDomain}
            </Text>
          </Box>
        </Flex>

        {/* View Button */}
        <Link href={parsedUrl} isExternal _hover={{ textDecoration: 'none' }} w={{ base: '100%', sm: 'auto' }}>
          <Button
            size="sm"
            colorScheme="green"
            flexShrink={0}
            w={{ base: '100%', sm: 'auto' }}
            rightIcon={
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
          >
            View Website
          </Button>
        </Link>
      </Flex>
    );
  }

  // PROGRESS STATE: Full progress view with steps
  return (
    <Box
      borderRadius="xl"
      bg={bgColor}
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      maxW="650px"
      boxShadow="lg"
    >
      {/* Header with domain link */}
      {displayDomain && (
        <Flex
          align="center"
          justify="space-between"
          px={4}
          py={3}
          bg="gray.50"
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <HStack spacing={2}>
            <Box w="12px" h="12px" borderRadius="full" bg="red.400" />
            <Box w="12px" h="12px" borderRadius="full" bg="yellow.400" />
            <Box w="12px" h="12px" borderRadius="full" bg="green.400" />
          </HStack>
          <Flex
            align="center"
            bg="white"
            px={4}
            py={1.5}
            borderRadius="full"
            border="1px solid"
            borderColor="gray.200"
            flex="1"
            mx={4}
          >
            <Text
              fontSize="sm"
              color="gray.500"
              fontFamily="monospace"
              isTruncated
            >
              https://{displayDomain}
            </Text>
          </Flex>
        </Flex>
      )}

      {/* Progress section */}
      <Box p={5}>
        <Progress
          value={progressPercent}
          size="sm"
          colorScheme={isFailed ? 'red' : 'orange'}
          borderRadius="full"
          mb={5}
          isAnimated
          hasStripe
        />

        <VStack spacing={4} align="stretch" mb={5}>
          {STEPS.map((step, index) => (
            <StepIndicator
              key={step.key}
              step={step}
              index={index}
              currentStepIndex={currentStepIndex}
              isComplete={false}
              isFailed={isFailed}
            />
          ))}
        </VStack>

        <Flex justify="center" mb={4}>
          {isFailed ? (
            <Badge colorScheme="red" fontSize="sm" px={3} py={1} borderRadius="full">
              Generation encountered an issue - We&apos;ll contact you with results
            </Badge>
          ) : (
            <Badge colorScheme="orange" fontSize="sm" px={3} py={1} borderRadius="full">
              Creating your website...
            </Badge>
          )}
        </Flex>
      </Box>

      {/* Preview iframe */}
      {showIframe && (
        <Box
          position="relative"
          h={showFullPreview ? '500px' : '300px'}
          transition="height 0.3s ease"
          borderTop="1px solid"
          borderColor={borderColor}
        >
          {!iframeLoaded && (
            <Flex
              position="absolute"
              top="0"
              left="0"
              right="0"
              bottom="0"
              align="center"
              justify="center"
              bg="gray.50"
              zIndex={1}
            >
              <VStack spacing={3}>
                <Spinner size="lg" color="orange.500" />
                <Text fontSize="sm" color="gray.500">Loading preview...</Text>
              </VStack>
            </Flex>
          )}
          <iframe
            ref={iframeRef}
            src={parsedUrl}
            title="Website Preview"
            width="100%"
            height="100%"
            style={{
              border: 'none',
              transform: 'scale(0.75)',
              transformOrigin: 'top left',
              width: '133.33%',
              height: '133.33%',
            }}
            onLoad={handleIframeLoad}
          />

          <Button
            position="absolute"
            bottom="10px"
            right="10px"
            size="sm"
            variant="solid"
            bg="white"
            boxShadow="md"
            onClick={() => setShowFullPreview(!showFullPreview)}
            fontFamily="'Noto Sans', 'Open Sans', sans-serif"
          >
            {showFullPreview ? 'Show less' : 'Show more'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default WebsitePreview;
